import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9pcl6osenbniolessu0';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.meta
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
          : '';
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const breadcrumbItems = [
    { name: '首页', href: '/' },
    { name: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': '关于韩国漫画网',
    'description': '韩国漫画网是最全的韩漫网站，提供免费漫画在线观看服务。了解我们的品牌故事、口碑评价与联系方式。',
    'url': 'https://www.koreamanhua.com/about',
    'inLanguage': 'zh-Hans',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '韩国漫画网',
    'url': 'https://www.koreamanhua.com',
    'description': '最全的韩国漫画在线阅读平台，提供免费韩国漫画在线观看服务',
    'foundingDate': '2020',
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'email': 'contact@koreamanhua.com',
      'availableLanguage': ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">关于韩国漫画网</h1>
              <p className="text-sm text-foreground-500 mb-8">最全的韩漫网站，您的韩国漫画阅读首选平台</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
                  <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      韩国漫画网成立于2020年，是国内最早专注于韩国漫画资源整合与在线阅读的平台之一。我们致力于为广大韩漫爱好者提供<strong>最全面、最及时、最优质</strong>的韩国漫画阅读服务。
                    </p>
                    <p>
                      经过多年发展，韩国漫画网已建立起覆盖恋爱、奇幻、动作、搞笑、悬疑、剧情、恐怖等全类型的韩漫资源库，收录作品超过千部，每日同步更新，确保读者能够在第一时间欣赏到最新的韩国漫画内容。
                    </p>
                    <p>
                      我们的团队由资深的漫画爱好者和互联网技术专家组成，深耕韩国漫画领域多年，深刻理解读者的需求与喜好。无论是热门连载还是经典完结作品，在韩国漫画网都能找到。
                    </p>
                    <p>
                      韩国漫画网坚持<strong>免费漫画在线观看</strong>的核心理念，让每一位喜爱韩国漫画的读者都能无障碍地享受阅读乐趣。同时，我们持续优化网站体验，打造简洁清爽的阅读界面，让韩漫阅读成为一种享受。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden h-72 md:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20beautiful%20illustration%20of%20a%20cozy%20reading%20corner%20with%20bookshelves%20and%20warm%20lighting%2C%20soft%20coral%20and%20cream%20tones%2C%20digital%20art%2C%20peaceful%20atmosphere%2C%20Korean%20webtoon%20aesthetic%2C%20clean%20minimal%20design&width=800&height=500&seq=about-brand-img&orientation=landscape"
                    alt="韩国漫画网 - 品牌文化"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <section className="mb-12">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">核心优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { icon: 'ri-database-2-line', title: '资源丰富', desc: '收录上千部韩国漫画，覆盖全类型' },
                    { icon: 'ri-flashlight-line', title: '更新及时', desc: '每日同步最新话，与韩网同步更新' },
                    { icon: 'ri-eye-line', title: '免费阅读', desc: '全部漫画免费在线观看，无需付费' },
                    { icon: 'ri-smartphone-line', title: '多端适配', desc: '支持电脑、平板、手机多设备阅读' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-lg p-5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-xs text-foreground-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: '漫画迷小王', rating: 5, text: '韩国漫画网真的是我用过最好的韩漫网站！资源超全，更新速度也很快，界面干净没有乱七八糟的广告，推荐给所有喜欢看韩漫的朋友。' },
                    { name: '韩剧控', rating: 5, text: '自从发现了韩国漫画网，就再也没去过其他平台。分类清晰，找漫画特别方便，而且全部免费，真的是良心网站！' },
                    { name: '追漫达人', rating: 5, text: '作为追了5年韩漫的老读者，韩国漫画网的漫画最全，更新也最及时。很多在其他平台找不到的冷门好作品这边都有，非常棒！' },
                  ].map((review) => (
                    <div key={review.name} className="bg-background-100 rounded-lg p-5">
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: review.rating }, (_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">{review.text}</p>
                      <p className="text-xs text-foreground-500 font-medium">—— {review.name}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">数据一览</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { value: '1000+', label: '收录漫画' },
                    { value: '285万+', label: '累计阅读' },
                    { value: '50万+', label: '活跃读者' },
                    { value: '99.7%', label: '好评率' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-background-100 rounded-lg p-5 text-center">
                      <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</p>
                      <p className="text-xs text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                        <p className="text-sm text-foreground-500">contact@koreamanhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                        <p className="text-sm text-foreground-500">韩国首尔特别市江南区</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">工作时间</h3>
                        <p className="text-sm text-foreground-500">每日 09:00 - 22:00（韩国标准时间）</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <form onSubmit={handleSubmit} data-readdy-form className="bg-background-100 rounded-lg p-5 md:p-6">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-4">发送消息</h3>

                      {formSubmitted && (
                        <div className="mb-4 px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg">
                          消息已发送成功，我们会尽快回复您！
                        </div>
                      )}

                      {formError && (
                        <div className="mb-4 px-4 py-3 bg-red-50 text-red-700 text-sm rounded-lg">
                          {formError}
                        </div>
                      )}

                      <div className="space-y-3">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的姓名"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请输入您的留言内容..."
                            rows={4}
                            maxLength={500}
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all resize-none"
                          ></textarea>
                        </div>

                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="hp-field"
                        />

                        <button
                          type="submit"
                          className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-all whitespace-nowrap"
                        >
                          发送消息
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </section>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}